import { useState, useContext } from 'react'
import { ImperialBookingContext } from '../context/ImperialBookingContext'
import { Auth } from 'aws-amplify'
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import useResident from './useResident'

// https://aws-amplify.github.io/amplify-js/api/classes/authclass.html

const useAuth = () => {
  const {
    email,
    setEmail,
    password,
    setPassword,

    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone,
    unit,
    setUnitNumber,
    hasPadSetUp,
    setHasPadSetUp,

    authError,
    setAuthError,
    authNewPassword,
    setAuthNewPassword,
    authResetPassword,
    setAuthResetPassword,
    hasYetToConfirmEmail,
    setHasYetToConfirmEmail,

    user,
    setCognitoUser,

    showFacebook,
  } = useContext(ImperialBookingContext)
  const { saveResident } = useResident()

  const [authInfo, setAuthInfo] = useState('')
  const [limboUser, setLimboUser] = useState(null)

  const clearMessages = () => {
    setAuthError('')
    setAuthInfo('')
  }

  if (process.env.REACT_APP_DEBUG) {
    window._Auth = Auth
  }

  const signIn = async (email, password) => {
    let success = false
    clearMessages()
    try {
      const user = await Auth.signIn(email, password)
      if (user.getSignInUserSession() === null && user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        const { requiredAttributes } = user.challengeParam
        console.log('requiredAttributes', requiredAttributes)
        setAuthError('Please choose a new password and complete remaining information.')
        setAuthNewPassword(true)
        setLimboUser(user)
        window.limboUser = user
      } else {
        setCognitoUser(user)
        success = true
      }
    } catch (err) {
      setAuthError(err.message)
      console.log(err)
      if (err.code === 'PasswordResetRequiredException') {
        setAuthResetPassword(true)
      } else if (err.code === 'NotAuthorizedException') {
        setAuthError('Invalid email or password.')
      } else if (err.code === 'UserNotConfirmedException') {
        setAuthError('Your email has not yet been confirmed.')
        setHasYetToConfirmEmail(true)
        throw err
      }
    }

    return success
  }

  const signUp = async ({ firstName, lastName }) => {
    clearMessages()
    console.log('Signing up with', firstName, lastName)
    try {
      const result = await Auth.signUp({
        username: email,
        password,
        attributes: {
          given_name: firstName,
          family_name: lastName,
        }
      })

      console.log('result', result)

      return result
    } catch (err) {
      console.log('Failed because', err)
      setAuthError(err.message)
    }
  }

  const signUpConfirm = async (code) => {
    clearMessages()
    try {
      const result = await Auth.confirmSignUp(email, code)

      await signIn(email, password)
      await saveResident()

      return result
    } catch (err) {
      console.log(err.message)
      setAuthError(err.message)
    }
  }

  const resendCode = async (message = 'Verification code resent.') => {
    clearMessages()
    try {
      const result = await Auth.resendSignUp(email)
      setAuthInfo(message)
      return result
    } catch (err) {
      console.log(err.message)
      setAuthError(err.message)
    }
  }

  const completeNewPassword = async (newPassword) => {
    clearMessages()
    debugger

    try {
      const user = await Auth.completeNewPassword(limboUser, newPassword, {
        given_name: firstName,
        family_name: lastName,
      })
      //console.log('finalUser', user)
      window.user = user
      setCognitoUser(user)
      setAuthNewPassword(false)
      return true
    } catch (err) {
      console.log(err)
      setAuthError(err.message)
    }
  }

  const forgotPassword = async (email) => {
    clearMessages()
    let forgotResult
    try {
      forgotResult = Auth.forgotPassword(email)
      forgotResult.then(() => {
        setAuthError("Please check your email for a reset code.")
        setAuthResetPassword(true)
      })
      forgotResult.catch(() => {
        setAuthError("No account found with this email. Please try another.")
        setAuthResetPassword(false)
      })
    } catch (err) {
      setAuthError(err.message)
      setAuthResetPassword(true)
    }
    return forgotResult
  }

  const cancelForgotPassword = () => {
    setAuthResetPassword(false)
  }

  const resetPassword = async (email, code, newPassword) => {
    clearMessages()
    try {
      return Auth.forgotPasswordSubmit(email, code, newPassword)
        .catch((err) => {
          //debugger
          if (err.code === 'ExpiredCodeException') {
            setAuthError("This code has expired. Please check your email for a new one.")
            forgotPassword(email)
          } else if (err.code === 'LimitExceededException') {
            setAuthError("Too many failed attempts. Please try again a bit later.")
            forgotPassword(email)
          }
        })
    } catch (err) {
      console.log(err.message)
      setAuthError(err.message)
    }
  }

  const changePassword = async (oldPassword, newPassword) => {
    clearMessages()
    try {
      return Auth.changePassword(user, oldPassword, newPassword)
        .catch((err) => {
          if (err.code === 'NotAuthorizedException') {
            setAuthError(err.message)
          }
          else if (err.code === 'LimitExceededException') {
            setAuthError('Too many failed attempts. Please try again later.')
          }
          else {
            setAuthError(err.message)
          }
        })
    } catch (err) {
      console.log(err.message)
      setAuthError(err.message)
    }
  }

  const facebookSignin = () => {
    return Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Facebook })
  }

  return {
    isSignedIn: !!user,
    email,
    setEmail,
    password,
    setPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone,
    unit,
    setUnitNumber,
    hasPadSetUp,
    setHasPadSetUp,
    signIn,
    signUp,
    resendCode,
    signUpConfirm,
    authError,
    authInfo,
    setAuthError,
    hasYetToConfirmEmail,

    authNewPassword,
    completeNewPassword,

    authResetPassword,
    resetPassword,
    forgotPassword,
    cancelForgotPassword,
    changePassword,

    facebookSignin,
    showFacebook,
  }
}

export default useAuth
